import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../utils/firebase';
import { collection, getDocs, getDoc, addDoc, setDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy, where, limit, Timestamp } from 'firebase/firestore';
import { searchFoods, buildServingOptions } from '../utils/usda';
import { useFoodPhotoScan } from '../hooks/useFoodPhotoScan';
import { getDailyWeeklyMonthlyRanges } from '../utils/dateRanges';
import { toMillis } from '../utils/timestamps';
import { getDateLabel, getDateKey } from '../utils/dateDivider';
import logger from '../utils/logger';
import { useToast } from '../context/ToastContext';
import ModalBackdrop from './ModalBackdrop';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

// Hard cap on how many foods a user can pin to the Pinned row. Keeping it
// small (≤6) so the row still fits horizontally on narrow mobile screens
// without wrapping into a second line that pushes the search bar down.
const PINNED_MEALS_MAX = 6;

// Sortable meal row with drag handle
function SortableMealRow({ meal, formatDate, onClickMeal }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: meal.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="data-table-row" onClick={() => onClickMeal(meal)}>
      <span className="data-table-cell" data-label="Date">{meal.date || formatDate(meal.timestamp)}</span>
      <span className="data-table-cell" data-label="Food">{meal.foodName}</span>
      <span className="data-table-cell" data-label="Calories">{meal.calories} cal</span>
      <span
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        className="drag-handle"
        onClick={(e) => e.stopPropagation()}
        aria-label="Drag to reorder"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="8" y1="18" x2="16" y2="18"/>
        </svg>
      </span>
    </div>
  );
}

function MealLog({ userId, onCaloriesChange, onMealLogged, onError }) {
  const showToast = useToast();
  const [meals, setMeals] = useState([]);
  const [loadingMeals, setLoadingMeals] = useState(false);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editCalories, setEditCalories] = useState('');
  const [editDate, setEditDate] = useState('');
  const [recentFoods, setRecentFoods] = useState([]);
  const [pinnedMeals, setPinnedMeals] = useState([]);
  // pillContextMenu shape: { meal, x, y, type } where type is 'recent' or 'pinned'
  const [pillContextMenu, setPillContextMenu] = useState(null);
  const pillContextMenuRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const [showAllMealsPopup, setShowAllMealsPopup] = useState(false);
  const [allMealsPage, setAllMealsPage] = useState(1);

  // Manual entry
  const [manualName, setManualName] = useState('');
  const [manualCalories, setManualCalories] = useState('');

  // USDA search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedFood, setSelectedFood] = useState(null);
  const [servingOptions, setServingOptions] = useState([]);
  const [selectedServing, setSelectedServing] = useState(null);
  const debounceRef = useRef(null);

  // Food photo scan — sends image to Ollama vision model via UM890 proxy.
  // Success pre-fills the USDA search; failure shows an inline "unavailable"
  // message without blocking manual entry.
  const { scan: scanPhoto, scanning: photoScanning, unavailable: photoUnavailable, reset: resetPhotoScan } = useFoodPhotoScan();
  // Two separate file inputs so we can toggle the `capture` attribute. Mobile
  // browsers route capture="environment" to the rear camera, while omitting
  // capture entirely opens the photo library / file picker.
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const photoMenuRef = useRef(null);
  const photoButtonRef = useRef(null);
  const [photoMenuOpen, setPhotoMenuOpen] = useState(false);
  const [photoSuggestion, setPhotoSuggestion] = useState(null); // { foodName, servingSize, confidence }
  const manualDetailsRef = useRef(null);

  // Takes the pinned list as a parameter rather than reading from state so
  // callers can pass a freshly-mutated array (e.g. right after pin/unpin)
  // without waiting for a re-render. Keeping this useCallback depending
  // only on `userId` avoids the feedback loop that kicks off when pinnedMeals
  // is in the dep array and setPinnedMeals creates new array references.
  const fetchRecentFoods = useCallback(async (currentPinnedMeals = []) => {
    if (!userId) return;
    try {
      const sevenDaysAgo = Timestamp.fromMillis(Date.now() - 7 * 24 * 60 * 60 * 1000);
      const q = query(
        collection(db, 'users', userId, 'meals'),
        where('timestamp', '>=', sevenDaysAgo),
        orderBy('timestamp', 'desc'),
        limit(50)
      );
      const snapshot = await getDocs(q);
      const docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const seen = new Map();
      for (const m of docs) {
        const key = (m.foodName || '').toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.set(key, m);
        if (seen.size >= 8) break;
      }
      // Hide any recent food that's already pinned — avoids showing the
      // same food twice across the Pinned and Recent rows.
      const filtered = Array.from(seen.values()).filter(m =>
        !currentPinnedMeals.some(p => p.foodName.toLowerCase() === m.foodName.toLowerCase())
      );
      setRecentFoods(filtered);
    } catch (error) {
      logger.error(error, { page: 'User', action: 'fetchRecentFoods' });
    }
  }, [userId]);

  // Load the user's pinned meals from preferences/pinnedMeals. Capped at
  // PINNED_MEALS_MAX in case the Firestore doc somehow contains more than
  // the current limit (e.g. the cap was lowered in a later release).
  // Returns the capped list so callers can chain it into fetchRecentFoods
  // without a state round-trip.
  const fetchPinnedMeals = useCallback(async () => {
    if (!userId) return [];
    try {
      const snap = await getDoc(doc(db, 'users', userId, 'preferences', 'pinnedMeals'));
      if (snap.exists()) {
        const list = Array.isArray(snap.data().meals) ? snap.data().meals : [];
        const capped = list.slice(0, PINNED_MEALS_MAX);
        setPinnedMeals(capped);
        return capped;
      } else {
        setPinnedMeals([]);
        return [];
      }
    } catch (error) {
      logger.error(error, { page: 'User', action: 'fetchPinnedMeals' });
      return [];
    }
  }, [userId]);

  // Fetch meals on mount
  useEffect(() => {
    if (!userId) return;

    const fetchMeals = async () => {
      setLoadingMeals(true);
      try {
        const snapshot = await getDocs(collection(db, 'users', userId, 'meals'));
        const mealData = snapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => toMillis(b.timestamp) - toMillis(a.timestamp));
        setMeals(mealData);
      } catch (error) {
        logger.error(error, { page: 'User', action: 'fetchMeals' });
        onError('Failed to fetch meals. Please try again.');
      } finally {
        setLoadingMeals(false);
      }
    };

    fetchMeals();
    // Chain: load pinned first, then pass the fetched list directly into
    // fetchRecentFoods so the filter reflects the actual current pins
    // without a state round-trip (which would create a feedback loop).
    (async () => {
      const pinned = await fetchPinnedMeals();
      fetchRecentFoods(pinned);
    })();
  }, [userId, onError, fetchRecentFoods, fetchPinnedMeals]);

  // Lift today's consumed calories to User.js whenever meals change
  useEffect(() => {
    const { todayStart } = getDailyWeeklyMonthlyRanges();
    const todayCalories = meals
      .filter(m => toMillis(m.timestamp) >= todayStart)
      .reduce((sum, m) => sum + (m.calories || 0), 0);
    onCaloriesChange(todayCalories);
  }, [meals, onCaloriesChange]);

  // Debounced USDA search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!searchQuery || searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      const results = await searchFoods(searchQuery);
      setSearchResults(results);
      setSearching(false);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  // Reset popup pagination on open
  useEffect(() => {
    if (showAllMealsPopup) setAllMealsPage(1);
  }, [showAllMealsPopup]);

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    const name = manualName.trim();
    if (!name) { onError('Food name is required.'); return; }
    const cal = parseInt(manualCalories);
    if (isNaN(cal) || cal < 0) { onError('Calories must be a non-negative number.'); return; }
    if (!userId) { onError('User ID not found.'); return; }

    const nowMs = Date.now();
    const meal = { foodName: name, calories: cal, source: 'manual' };
    try {
      const docRef = await addDoc(collection(db, 'users', userId, 'meals'), {
        ...meal,
        timestamp: serverTimestamp(),
      });
      setMeals(prev => [{
        id: docRef.id, ...meal, timestamp: nowMs,
        date: new Date(nowMs).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
      }, ...prev]);
      setManualName('');
      setManualCalories('');
      setSearchQuery('');
      setPhotoSuggestion(null);
      fetchRecentFoods(pinnedMeals);
      onMealLogged?.();
    } catch (error) {
      logger.error(error, { page: 'User', action: 'logMeal' });
      onError('Failed to log meal. Please try again.');
    }
  };

  // When the user picks an image, send it to the vision model. On a
  // successful identification, prefill the USDA search with the returned
  // food name — the existing debounced search effect picks it up and
  // returns results like any typed query would. Low-confidence "unknown"
  // results short-circuit so we don't run a useless search.
  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    // Reset the input value so choosing the same file again still fires.
    e.target.value = '';
    if (!file) return;
    setPhotoSuggestion(null);
    resetPhotoScan();
    const result = await scanPhoto(file);
    if (!result) return; // unavailable state handled by hook
    if (result.foodName === 'unknown' && result.confidence === 'low') {
      // Don't run a doomed USDA search; show the unavailable message instead.
      setPhotoSuggestion(null);
      return;
    }
    setPhotoSuggestion({
      foodName: result.foodName,
      servingSize: result.estimatedServingSize,
      estimatedCalories: result.estimatedCalories,
      confidence: result.confidence,
    });
    setSelectedFood(null);
    setServingOptions([]);
    setSelectedServing(null);
    // Populate the search box — the debounced effect runs the USDA query.
    setSearchQuery(result.foodName);
  };

  const handlePhotoButtonClick = () => {
    if (photoScanning) return;
    setPhotoMenuOpen(prev => !prev);
  };

  const handleTakePhoto = () => {
    setPhotoMenuOpen(false);
    cameraInputRef.current?.click();
  };

  const handleChooseFromGallery = () => {
    setPhotoMenuOpen(false);
    galleryInputRef.current?.click();
  };

  // Dismiss the photo menu when the user clicks/taps outside it.
  // Listening on `mousedown` (not `click`) so the popover closes before any
  // outside click handler runs, matching standard popover dismissal UX.
  useEffect(() => {
    if (!photoMenuOpen) return;
    const handleOutside = (e) => {
      const menu = photoMenuRef.current;
      const button = photoButtonRef.current;
      if (menu && menu.contains(e.target)) return;
      if (button && button.contains(e.target)) return;
      setPhotoMenuOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [photoMenuOpen]);

  const handleUSDASelect = (food) => {
    const options = buildServingOptions(food);
    setSelectedFood(food);
    setServingOptions(options);
    // If a photo scan suggested a serving size, try to match it against the
    // USDA options by substring. Fall back to the first option if nothing
    // matches — the user can still pick manually.
    let initialServing = options[0];
    if (photoSuggestion?.servingSize && photoSuggestion.servingSize !== 'unknown') {
      const hint = photoSuggestion.servingSize.toLowerCase();
      const match = options.find(o => (o.label || '').toLowerCase().includes(hint));
      if (match) initialServing = match;
    }
    setSelectedServing(initialServing);
    setSearchResults([]);
    setSearchQuery('');
    setPhotoSuggestion(null);
  };

  const handleUSDASubmit = async () => {
    if (!selectedFood || !selectedServing || !userId) return;

    const isManualRepeat = !selectedFood.fdcId;
    const nowMs = Date.now();
    const meal = {
      foodName: selectedFood.foodName,
      calories: selectedServing.calories,
      ...(isManualRepeat ? {} : { grams: selectedServing.grams, fdcId: selectedFood.fdcId }),
      source: isManualRepeat ? 'manual' : 'usda',
    };
    try {
      const docRef = await addDoc(collection(db, 'users', userId, 'meals'), {
        ...meal,
        timestamp: serverTimestamp(),
      });
      setMeals(prev => [{
        id: docRef.id, ...meal, timestamp: nowMs,
        date: new Date(nowMs).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
      }, ...prev]);
      setSelectedFood(null);
      setServingOptions([]);
      setSelectedServing(null);
      setPhotoSuggestion(null);
      fetchRecentFoods(pinnedMeals);
      onMealLogged?.();
    } catch (error) {
      logger.error(error, { page: 'User', action: 'logMealUSDA' });
      onError('Failed to log meal. Please try again.');
    }
  };

  // Pin a meal to the Pinned row. Writes the full shape needed by
  // handleRecentFoodClick (foodName, calories, source, fdcId, grams) so
  // pinned pills reopen the serving picker the same way recent pills do.
  const handlePin = async (meal) => {
    if (pinnedMeals.length >= PINNED_MEALS_MAX) {
      showToast('Pinned list is full — unpin something to add this.', 'error');
      return;
    }
    const alreadyPinned = pinnedMeals.some(p => p.foodName.toLowerCase() === meal.foodName.toLowerCase());
    if (alreadyPinned) return;
    const newPinned = [
      ...pinnedMeals,
      {
        foodName: meal.foodName,
        calories: meal.calories,
        source: meal.source,
        fdcId: meal.fdcId || null,
        grams: meal.grams || null,
      },
    ];
    setPinnedMeals(newPinned);
    fetchRecentFoods(newPinned);
    try {
      await setDoc(doc(db, 'users', userId, 'preferences', 'pinnedMeals'), { meals: newPinned });
      showToast(`${meal.foodName} pinned.`, 'success');
    } catch (error) {
      logger.error(error, { page: 'User', action: 'pinMeal' });
    }
  };

  const handleUnpin = async (meal) => {
    const newPinned = pinnedMeals.filter(p => p.foodName.toLowerCase() !== meal.foodName.toLowerCase());
    setPinnedMeals(newPinned);
    fetchRecentFoods(newPinned);
    try {
      await setDoc(doc(db, 'users', userId, 'preferences', 'pinnedMeals'), { meals: newPinned });
      showToast(`${meal.foodName} unpinned.`, 'success');
    } catch (error) {
      logger.error(error, { page: 'User', action: 'unpinMeal' });
    }
  };

  // Opens the pill context menu at the pointer (or first touch) coordinates.
  // Called from onContextMenu (desktop right-click) and the long-press touch
  // timer (mobile). The synthetic event from the timer carries the stored
  // touch position so the menu anchors under the user's finger.
  const handlePillLongPress = (meal, e, type) => {
    if (e && e.preventDefault) e.preventDefault();
    const x = e?.clientX || e?.touches?.[0]?.clientX || 0;
    const y = e?.clientY || e?.touches?.[0]?.clientY || 0;
    setPillContextMenu({ meal, x, y, type });
  };

  // Touch long-press detection — starts a 500ms timer on touchstart and
  // cancels it on touchend/touchmove so a normal tap or scroll doesn't
  // trigger the context menu. The timer fires with a synthetic event that
  // carries the original touch coordinates.
  const handlePillTouchStart = (meal, e, type) => {
    e.preventDefault();
    const touch = e.touches?.[0];
    const x = touch?.clientX || 0;
    const y = touch?.clientY || 0;
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      handlePillLongPress(meal, { clientX: x, clientY: y, preventDefault: () => {} }, type);
    }, 500);
  };

  const handlePillTouchCancel = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  // Dismiss the pill context menu on outside click/tap. Mirrors the
  // photoMenuOpen pattern — listens on mousedown + touchstart so the menu
  // closes before any underlying click handler can run.
  useEffect(() => {
    if (!pillContextMenu) return;
    const handleOutside = (e) => {
      const menu = pillContextMenuRef.current;
      if (menu && menu.contains(e.target)) return;
      setPillContextMenu(null);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [pillContextMenu]);

  // When a recent-food pill is clicked, open the serving picker.
  // USDA entries rebuild serving options from stored kcalPer100g data;
  // manual entries show a simple calorie input pre-filled with last value.
  const handleRecentFoodClick = (meal) => {
    if (meal.source === 'usda' && meal.fdcId) {
      // Reconstruct the trimmedFood shape that buildServingOptions expects.
      // kcalPer100g is derived from stored calories and grams if both exist,
      // otherwise fall back to calories-per-100g (the only safe assumption).
      const kcalPer100g = meal.grams
        ? Math.round((meal.calories / meal.grams) * 100)
        : meal.calories;
      const food = {
        fdcId: meal.fdcId,
        foodName: meal.foodName,
        kcalPer100g,
        servingGrams: null,
      };
      const options = buildServingOptions(food);
      setSelectedFood(food);
      setServingOptions(options);
      setSelectedServing(options[0]);
    } else {
      // Manual entry: show serving picker with a single option pre-filled
      // with the last logged calorie amount, matching the USDA display pattern.
      const food = {
        fdcId: null,
        foodName: meal.foodName,
        kcalPer100g: null,
        servingGrams: null,
      };
      const options = [{
        label: `${meal.calories} cal (last logged)`,
        grams: 0,
        calories: meal.calories,
      }];
      setSelectedFood(food);
      setServingOptions(options);
      setSelectedServing(options[0]);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMeal) return;
    const cal = parseInt(editCalories);
    if (isNaN(cal) || cal < 0) { onError('Calories must be a non-negative number.'); return; }
    if (!editDate) { onError('Date is required.'); return; }
    const newTimestamp = new Date(editDate + 'T12:00:00');

    try {
      const mealRef = doc(db, 'users', userId, 'meals', selectedMeal.id);
      await updateDoc(mealRef, { calories: cal, timestamp: newTimestamp });
      setMeals(prev => prev.map(m =>
        m.id === selectedMeal.id ? { ...m, calories: cal, timestamp: newTimestamp.getTime() } : m
      ));
      setSelectedMeal(null);
      setEditing(false);
      showToast('Meal updated successfully!', 'success');
    } catch (error) {
      logger.error(error, { page: 'User', action: 'editMeal' });
      onError('Failed to update meal. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!selectedMeal) return;
    try {
      await deleteDoc(doc(db, 'users', userId, 'meals', selectedMeal.id));
      setMeals(prev => prev.filter(m => m.id !== selectedMeal.id));
      setSelectedMeal(null);
      setEditing(false);
      fetchRecentFoods(pinnedMeals);
      onMealLogged?.();
    } catch (error) {
      logger.error(error, { page: 'User', action: 'deleteMeal' });
      onError('Failed to delete meal. Please try again.');
    }
  };

  const openMealDetails = (meal) => {
    setSelectedMeal(meal);
    setEditing(false);
    setEditCalories(meal.calories.toString());
    const d = new Date(toMillis(meal.timestamp));
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    setEditDate(`${yyyy}-${mm}-${dd}`);
  };

  const formatDate = (ts) =>
    new Date(toMillis(ts)).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });

  return (
    <>
      <div className="weight-tracking">
        <h3>Food Log</h3>

        {/* Pinned meals pills — long-press or right-click to unpin */}
        {pinnedMeals.length > 0 && (
          <div className="meal-recent-foods">
            <span className="meal-recent-label">Pinned:</span>
            {pinnedMeals.map((m, i) => (
              <button
                key={`pinned-${i}`}
                className="meal-recent-pill meal-pinned-pill"
                style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
                onClick={() => handleRecentFoodClick(m)}
                onContextMenu={(e) => handlePillLongPress(m, e, 'pinned')}
                onTouchStart={(e) => handlePillTouchStart(m, e, 'pinned')}
                onTouchEnd={handlePillTouchCancel}
                onTouchMove={handlePillTouchCancel}
                onTouchCancel={handlePillTouchCancel}
              >
                {m.foodName.length > 25 ? m.foodName.slice(0, 25) + '\u2026' : m.foodName}
              </button>
            ))}
          </div>
        )}

        {/* Recent foods pills — long-press or right-click to pin */}
        {recentFoods.length > 0 && (
          <div className="meal-recent-foods">
            <span className="meal-recent-label">Recent:</span>
            {recentFoods.map(m => (
              <button
                key={m.id}
                className="meal-recent-pill"
                style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
                onClick={() => handleRecentFoodClick(m)}
                onContextMenu={(e) => handlePillLongPress(m, e, 'recent')}
                onTouchStart={(e) => handlePillTouchStart(m, e, 'recent')}
                onTouchEnd={handlePillTouchCancel}
                onTouchMove={handlePillTouchCancel}
                onTouchCancel={handlePillTouchCancel}
              >
                {m.foodName.length > 25 ? m.foodName.slice(0, 25) + '\u2026' : m.foodName}
              </button>
            ))}
          </div>
        )}

        {/* USDA search + photo scan */}
        <div className="meal-search">
          <div className="meal-search-row">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); if (photoSuggestion) setPhotoSuggestion(null); }}
              placeholder={photoScanning ? 'Scanning...' : 'Search foods (e.g. banana, chicken breast)'}
              disabled={photoScanning}
            />
            <div className="meal-photo-wrap">
              <button
                ref={photoButtonRef}
                type="button"
                className="meal-photo-button"
                onClick={handlePhotoButtonClick}
                disabled={photoScanning}
                aria-haspopup="menu"
                aria-expanded={photoMenuOpen}
                aria-label="Scan food photo"
                title="Scan food photo"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
              </button>
              {photoMenuOpen && (
                <div ref={photoMenuRef} className="meal-photo-menu" role="menu">
                  <button type="button" className="meal-photo-menu-item" role="menuitem" onClick={handleTakePhoto}>
                    <span className="meal-photo-menu-icon" aria-hidden="true">📷</span>
                    <span>Take Photo</span>
                  </button>
                  <button type="button" className="meal-photo-menu-item" role="menuitem" onClick={handleChooseFromGallery}>
                    <span className="meal-photo-menu-icon" aria-hidden="true">🖼️</span>
                    <span>Choose from Gallery</span>
                  </button>
                </div>
              )}
            </div>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handlePhotoChange}
              style={{ display: 'none' }}
            />
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              style={{ display: 'none' }}
            />
          </div>
          {photoScanning && (
            <p className="meal-scan-status" style={{ fontSize: '0.85em', margin: '4px 0', color: '#94a3b8' }}>Scanning...</p>
          )}
          {photoUnavailable && !photoScanning && (
            <p className="meal-scan-status" style={{ fontSize: '0.85em', margin: '4px 0', color: '#94a3b8' }}>
              Scanner unavailable, please search manually
            </p>
          )}
          {photoSuggestion && !photoScanning && photoSuggestion.foodName !== 'unknown' && (
            <p
              className="meal-scan-info"
              style={{
                fontSize: '0.85em',
                margin: '4px 0',
                fontStyle: 'italic',
                color: '#fb923c',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onClick={() => {
                setManualName(photoSuggestion.foodName);
                setManualCalories(photoSuggestion.estimatedCalories.toString());
                setSearchResults([]);
                if (manualDetailsRef.current) {
                  manualDetailsRef.current.open = true;
                  manualDetailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
              }}
            >
              AI estimated ~{photoSuggestion.estimatedCalories} cal ·{' '}
              {photoSuggestion.confidence === 'high'
                ? 'High confidence'
                : photoSuggestion.confidence === 'medium'
                ? 'Double-check this looks right'
                : "We're not sure — please verify"}
              {' · Log AI estimate'}
            </p>
          )}
          {searching && <p className="loading" style={{ fontSize: '0.85em', margin: '4px 0' }}>Searching...</p>}
          {searchResults.length > 0 && (
            <ul className="meal-search-results">
              {searchResults.map(food => (
                <li key={food.fdcId} onClick={() => handleUSDASelect(food)}>
                  <span className="meal-search-name">{food.foodName}</span>
                  <span className="meal-search-cal">{food.kcalPer100g} kcal/100g</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Serving picker after USDA selection */}
        {selectedFood && (
          <div className="meal-serving-picker">
            <p className="meal-selected-food">
              <span className="meal-selected-food-check" aria-hidden="true">&#10003;</span>
              <span className="meal-selected-food-name">{selectedFood.foodName}</span>
            </p>
            <select
              value={servingOptions.indexOf(selectedServing)}
              onChange={(e) => setSelectedServing(servingOptions[parseInt(e.target.value)])}
            >
              {servingOptions.map((opt, i) => (
                <option key={i} value={i}>{opt.label} — {opt.calories} cal</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '8px' }}>
              <button className="btn-primary" onClick={handleUSDASubmit}>Log</button>
              <button className="btn-secondary" onClick={() => { setSelectedFood(null); setServingOptions([]); setSelectedServing(null); setPhotoSuggestion(null); }}>Cancel</button>
            </div>
          </div>
        )}

        {/* Manual fallback */}
        <details className="meal-manual-details" ref={manualDetailsRef}>
          <summary>Or enter manually</summary>
          <form onSubmit={handleManualSubmit} className="meal-manual-form">
            <input
              type="text"
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
              placeholder="Food name"
            />
            <input
              type="number"
              value={manualCalories}
              onChange={(e) => setManualCalories(e.target.value)}
              placeholder="Calories"
            />
            <button type="submit" className="btn-primary">Log</button>
          </form>
        </details>
      </div>

      {/* Recent meals table */}
      <div className="recent-activities">
        <div className="recent-activities-header">
          <h3>Recent Meals</h3>
          <span className="show-all-link" onClick={() => setShowAllMealsPopup(true)}>Show All</span>
        </div>
        <p className="activity-instruction">Select a meal to edit or delete.</p>
        {meals.length === 0 && loadingMeals ? (
          <p className="loading">Loading...</p>
        ) : meals.length === 0 ? (
          <div className="empty-state">
            <svg className="empty-state-icon" xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8h1a4 4 0 0 1 0 8h-1"/>
              <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/>
              <line x1="6" y1="1" x2="6" y2="4"/>
              <line x1="10" y1="1" x2="10" y2="4"/>
              <line x1="14" y1="1" x2="14" y2="4"/>
            </svg>
            <p className="empty-state-headline">No meals logged yet</p>
            <p className="empty-state-subtitle">Search for a food above or enter calories manually</p>
          </div>
        ) : (
          <div className="data-table data-table--meal">
            <div className="data-table-header">
              <span>Date</span><span>Food</span><span>Calories</span>
            </div>
            {meals
              .sort((a, b) => toMillis(b.timestamp) - toMillis(a.timestamp))
              .slice(0, 5)
              .map(meal => (
                <div key={meal.id} className="data-table-row" onClick={() => openMealDetails(meal)}>
                  <span className="data-table-cell" data-label="Date">{meal.date || formatDate(meal.timestamp)}</span>
                  <span className="data-table-cell" data-label="Food">{meal.foodName}</span>
                  <span className="data-table-cell" data-label="Calories">{meal.calories} cal</span>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Meal detail / edit / delete modal */}
      {selectedMeal && (
        <ModalBackdrop onClose={() => { setSelectedMeal(null); setEditing(false); }}>
          <div className="popup-content">
            <h3>Meal Details</h3>
            {editing ? (
              <form onSubmit={handleEditSubmit}>
                <p><strong>{selectedMeal.foodName}</strong></p>
                <label>
                  Calories:
                  <input
                    type="number"
                    value={editCalories}
                    onChange={(e) => setEditCalories(e.target.value)}
                  />
                </label>
                <label>
                  Date:
                  <input
                    type="date"
                    value={editDate}
                    onChange={(e) => setEditDate(e.target.value)}
                  />
                </label>
                <div className="button-group">
                  <button type="submit" className="btn-primary">Save</button>
                  <button type="button" className="btn-secondary" onClick={() => {
                    setEditing(false);
                    setEditCalories(selectedMeal.calories.toString());
                    const d = new Date(toMillis(selectedMeal.timestamp));
                    setEditDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
                  }}>Cancel</button>
                </div>
              </form>
            ) : (
              <>
                <p className="activity-date">{formatDate(selectedMeal.timestamp)}</p>
                <p><strong>{selectedMeal.foodName}</strong></p>
                <p>{selectedMeal.calories} cal{selectedMeal.grams ? ` (${selectedMeal.grams}g)` : ''}</p>
                <div className="button-group">
                  <button className="btn-primary" onClick={() => setEditing(true)}>Edit</button>
                  <button className="btn-danger" onClick={handleDelete}>Delete</button>
                  <button className="btn-secondary" onClick={() => { setSelectedMeal(null); setEditing(false); }}>Close</button>
                </div>
              </>
            )}
          </div>
        </ModalBackdrop>
      )}

      {/* All meals popup with drag-and-drop reordering */}
      {showAllMealsPopup && (
        <AllMealsPopup
          meals={meals}
          setMeals={setMeals}
          userId={userId}
          allMealsPage={allMealsPage}
          setAllMealsPage={setAllMealsPage}
          formatDate={formatDate}
          openMealDetails={openMealDetails}
          onClose={() => setShowAllMealsPopup(false)}
        />
      )}

      {/* Pill context menu — anchored to the pointer/touch position */}
      {pillContextMenu && (
        <div
          ref={pillContextMenuRef}
          className="pill-context-menu"
          style={{
            position: 'fixed',
            top: pillContextMenu.y,
            left: pillContextMenu.x,
            background: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '8px',
            padding: '4px 0',
            minWidth: '140px',
            zIndex: 1000,
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.5)',
          }}
          role="menu"
        >
          {pillContextMenu.type === 'recent' && (
            <button
              type="button"
              className="pill-context-menu-item"
              onClick={() => {
                handlePin(pillContextMenu.meal);
                setPillContextMenu(null);
              }}
              style={{
                width: '100%',
                padding: '8px 16px',
                fontSize: '14px',
                color: '#f1f5f9',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Pin this food
            </button>
          )}
          {pillContextMenu.type === 'pinned' && (
            <button
              type="button"
              className="pill-context-menu-item"
              onClick={() => {
                handleUnpin(pillContextMenu.meal);
                setPillContextMenu(null);
              }}
              style={{
                width: '100%',
                padding: '8px 16px',
                fontSize: '14px',
                color: '#f1f5f9',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Unpin
            </button>
          )}
        </div>
      )}
    </>
  );
}

// Separate component for the All Meals popup to keep drag-and-drop state
// isolated from the main MealLog component.
function AllMealsPopup({ meals, setMeals, userId, allMealsPage, setAllMealsPage, formatDate, openMealDetails, onClose }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  // Sort meals: within each date group, sortOrder (if set) takes priority,
  // then timestamp desc as fallback.
  const sortedMeals = [...meals].sort((a, b) => {
    const dayA = getDateKey(a.timestamp);
    const dayB = getDateKey(b.timestamp);
    if (dayA !== dayB) return toMillis(b.timestamp) - toMillis(a.timestamp);
    // Same day: use sortOrder if both have it, else timestamp
    const orderA = a.sortOrder ?? Infinity;
    const orderB = b.sortOrder ?? Infinity;
    if (orderA !== Infinity || orderB !== Infinity) return orderA - orderB;
    return toMillis(b.timestamp) - toMillis(a.timestamp);
  });

  const paged = sortedMeals.slice((allMealsPage - 1) * 20, allMealsPage * 20);

  // Group paged meals by date key for independent sortable contexts
  const groups = [];
  let currentGroup = null;
  for (const meal of paged) {
    const key = getDateKey(meal.timestamp);
    if (!currentGroup || currentGroup.key !== key) {
      currentGroup = { key, label: getDateLabel(meal.timestamp), items: [] };
      groups.push(currentGroup);
    }
    currentGroup.items.push(meal);
  }

  const handleDragEnd = async (event, groupItems) => {
    const { active, over } = event;
    if (!active || !over || active.id === over.id) return;

    const oldIndex = groupItems.findIndex(m => m.id === active.id);
    const newIndex = groupItems.findIndex(m => m.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(groupItems, oldIndex, newIndex);

    // Assign sortOrder to each item in this group based on new position
    const updates = reordered.map((meal, i) => ({ ...meal, sortOrder: i }));

    // Optimistic update: merge new sortOrders into meals state
    setMeals(prev => {
      const updated = [...prev];
      for (const u of updates) {
        const idx = updated.findIndex(m => m.id === u.id);
        if (idx !== -1) updated[idx] = { ...updated[idx], sortOrder: u.sortOrder };
      }
      return updated;
    });

    // Persist only the moved items' sortOrder to Firestore
    try {
      await Promise.all(
        updates.map(meal =>
          updateDoc(doc(db, 'users', userId, 'meals', meal.id), { sortOrder: meal.sortOrder })
        )
      );
    } catch (err) {
      logger.error(err, { page: 'User', action: 'reorderMeals' });
    }
  };

  return (
    <ModalBackdrop onClose={onClose}>
      <div className="popup-content all-activities-popup">
        <div className="popup-header">
          <h3>All Meals</h3>
          <button className="popup-close-button btn-secondary" onClick={onClose}>Close</button>
        </div>
        <p className="activity-instruction">Select a meal to edit or delete. Drag handle to reorder.</p>
        {sortedMeals.length === 0 ? (
          <p className="no-data">No meals yet.</p>
        ) : (
          <>
            <div className="data-table data-table--meal">
              <div className="data-table-header">
                <span>Date</span><span>Food</span><span>Calories</span>
              </div>
              {groups.map(group => (
                <div key={group.key} className="date-group">
                  <div className="date-divider"><div className="date-divider-line" /><span>{group.label}</span><div className="date-divider-line" /></div>
                  <div className="date-group-rows">
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={(event) => handleDragEnd(event, group.items)}
                      modifiers={[restrictToVerticalAxis]}
                    >
                      <SortableContext items={group.items.map(m => m.id)} strategy={verticalListSortingStrategy}>
                        {group.items.map(meal => (
                          <SortableMealRow
                            key={meal.id}
                            meal={meal}
                            formatDate={formatDate}
                            onClickMeal={openMealDetails}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  </div>
                </div>
              ))}
            </div>
            {sortedMeals.length > 20 && (
              <div className="pagination">
                <button className="btn-secondary" onClick={() => setAllMealsPage(p => Math.max(p - 1, 1))} disabled={allMealsPage === 1}>Previous</button>
                <span>Page {allMealsPage} of {Math.ceil(sortedMeals.length / 20)}</span>
                <button className="btn-secondary" onClick={() => setAllMealsPage(p => Math.min(p + 1, Math.ceil(sortedMeals.length / 20)))} disabled={allMealsPage === Math.ceil(sortedMeals.length / 20)}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </ModalBackdrop>
  );
}

export default MealLog;
